import { Location } from 'history';
import { progressFromStep } from 'scenes/common/helpers/progress';

import { LocationState, ProgressBarFlow } from 'scenes/common/types';
import { LandlordFlowType, LandlordStepType } from 'scenes/landlord/types';

export const pathToStepType = (path: string) =>
  path
    ? (path.substring(path.lastIndexOf('/') + 1) as LandlordStepType)
    : LandlordStepType.CoverTypeSelection;

export const progressBarFlow: {
  [key in LandlordFlowType]: ProgressBarFlow;
} = {
  [LandlordFlowType.QuoteDetails]: ProgressBarFlow.GetQuote,
  [LandlordFlowType.CoverOptions]: ProgressBarFlow.GetQuote,
  [LandlordFlowType.PolicyDetails]: ProgressBarFlow.PolicyDetails,
  [LandlordFlowType.PaymentDetails]: ProgressBarFlow.BuyPolicy,
  [LandlordFlowType.ReviewDetails]: ProgressBarFlow.GetQuote,
};

const stepOrder: {
  [key in ProgressBarFlow]: LandlordStepType[];
} = {
  [ProgressBarFlow.GetQuote]: [
    LandlordStepType.CoverTypeSelection,
    LandlordStepType.PolicyStartDate,
    LandlordStepType.MainAddress,
    LandlordStepType.Ownership,
    LandlordStepType.PropertyPurpose,
    LandlordStepType.KickOutChoice,
    LandlordStepType.RentalAgreement,
    LandlordStepType.WeeklyRentPaid,
    LandlordStepType.PropertyUsedForBusiness,
    LandlordStepType.PersonalOrBusinessPolicy,
    LandlordStepType.ContactDateOfBirth,
    LandlordStepType.RecentInsurer,
    LandlordStepType.YearsInsured,
    LandlordStepType.ClaimsMade,
    LandlordStepType.BreakInAndAdditionalSecurity,
    LandlordStepType.BuildingType,
    LandlordStepType.NumberOfBedrooms,
    LandlordStepType.PropertyBuiltYear,
    LandlordStepType.RoofMaterial,
    LandlordStepType.WallMaterial,
    LandlordStepType.SecurityFeatures,
    LandlordStepType.SumInsuredChoice,
    LandlordStepType.SumInsuredBuildingValue,
    LandlordStepType.SumInsuredContentsValue,
    LandlordStepType.FlybuysNumber,
    LandlordStepType.PromoCode,
    LandlordStepType.QuotePrice,
    LandlordStepType.BuildingExcess,
    LandlordStepType.ContentsExcess,
    LandlordStepType.OptionalExtras,
  ],
  [ProgressBarFlow.PolicyDetails]: [
    LandlordStepType.HeritageListed,
    LandlordStepType.RewiredOrReplumbed,
    LandlordStepType.PropertyUnoccupied,
    LandlordStepType.PropertyRenovation,
    LandlordStepType.Over5Acres,
    LandlordStepType.PropertyOccupancy,
    LandlordStepType.PolicyHoldersHistory,
    LandlordStepType.MortgageProvider,
    LandlordStepType.PersonalPolicyContact,
    LandlordStepType.BusinessNameAndAbn,
    LandlordStepType.BusinessContact,
    LandlordStepType.ContactEmailAddress,
    LandlordStepType.ContactPhoneNumber,
    LandlordStepType.AddAdditionalPolicyHolder,
    LandlordStepType.AdditionalPolicyHolder,
    LandlordStepType.AdditionalPolicyHolderList,
    LandlordStepType.PostalAddress,
    LandlordStepType.DifferentPostalAddress,
    LandlordStepType.DifferentPostalAddressManual,
    LandlordStepType.LockedBagAddress,
    LandlordStepType.TermsAndConditions,
    LandlordStepType.Summary,
  ],
  [ProgressBarFlow.BuyPolicy]: [
    LandlordStepType.PaymentFrequency,
    LandlordStepType.PaymentDate,
    LandlordStepType.PaymentMethod,
    LandlordStepType.DirectDebitPayment,
    LandlordStepType.CreditCardPayment,
    LandlordStepType.PaymentConfirmation,
  ],
};

export const progress = (step: LandlordStepType, flow: LandlordFlowType) =>
  progressFromStep<LandlordStepType>(step, progressBarFlow[flow], stepOrder);

const stepsWithoutProgress: LandlordStepType[] = [
  LandlordStepType.PaymentConfirmation,
];

export const stepHasProgress = (
  location: Location<LocationState<LandlordFlowType>>
) => !stepsWithoutProgress.includes(pathToStepType(location.pathname));
